import { useMutation } from "@tanstack/react-query";
import { ChevronRight, Send, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { askAiApi, type AskAiAnswer } from "../api/collab";
import { ApiError } from "../api/client";
import { Button } from "../ds";

export interface AskAiPanelProps {
  reportId: string;
  /** The page the viewer is looking at (mrp:page). */
  pageId?: string | null;
  /** The filters currently applied (mrp:filters): [{ field, value, ... }]. */
  filters?: unknown[];
}

interface Turn {
  role: "user" | "assistant";
  text: string;
  widgets?: AskAiAnswer["widgetsUsed"];
  truncated?: boolean;
  error?: boolean;
}

const DEFAULT_SUGGESTIONS = ["Summarize this page", "What are the top risks?", "What changed the most?", "Which numbers look unusual?"];

/** Right-side collapsible "Ask AI" chat for the report viewer. Read-only: it
 * answers questions about the data the viewer is looking at (this page, these
 * filters) and shows which visuals it used. See docs/V3_0_ASK_AI_INTEGRATION.md. */
export function AskAiPanel({ reportId, pageId, filters }: AskAiPanelProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [turns, setTurns] = useState<Turn[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>(DEFAULT_SUGGESTIONS);
  const abort = useRef<AbortController | null>(null);
  const end = useRef<HTMLDivElement>(null);

  // A different report is a different conversation.
  useEffect(() => {
    setTurns([]);
    setSuggestions(DEFAULT_SUGGESTIONS);
    abort.current?.abort();
  }, [reportId]);
  useEffect(() => () => abort.current?.abort(), []);
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: "end" });
  }, [turns, open]);

  const ask = useMutation({
    mutationFn: (question: string) => {
      abort.current?.abort();
      abort.current = new AbortController();
      const history = turns.filter((t) => !t.error).slice(-6).map((t) => ({ role: t.role, text: t.text.slice(0, 1500) }));
      return askAiApi.ask(reportId, { question, pageId: pageId ?? null, filters: filters ?? [], history }, abort.current.signal);
    },
    onSuccess: (a) => {
      setTurns((t) => [...t, { role: "assistant", text: a.answer, widgets: a.widgetsUsed, truncated: a.truncated }]);
      if (a.suggestions?.length) setSuggestions(a.suggestions);
    },
    onError: (e) => {
      if (e instanceof DOMException && e.name === "AbortError") return;
      const message = e instanceof ApiError && e.status === 429 ? e.message : e instanceof ApiError && e.status === 503 ? "The AI assistant isn't available right now. Try again later." : e instanceof Error ? e.message : "The AI assistant didn't answer.";
      setTurns((t) => [...t, { role: "assistant", text: message, error: true }]);
    },
  });

  const send = (question: string) => {
    const q = question.trim();
    if (q.length < 2 || ask.isPending) return;
    setTurns((t) => [...t, { role: "user", text: q }]);
    setText("");
    ask.mutate(q);
  };

  if (!open) {
    return (
      <Button variant="primary" icon={Sparkles} onClick={() => setOpen(true)} aria-label="Ask AI about this report" style={{ position: "fixed", right: 16, bottom: 16, zIndex: 40 }}>
        Ask AI
      </Button>
    );
  }

  return (
    <aside aria-label="Ask AI" style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: 360, maxWidth: "100vw", zIndex: 40, display: "flex", flexDirection: "column", background: "var(--surface, #fff)", borderLeft: "1px solid var(--divider)", boxShadow: "-8px 0 24px rgba(0,0,0,.2)" }}>
      <div className="ds-row" style={{ padding: "12px 14px", borderBottom: "1px solid var(--divider)", gap: 8 }}>
        <Sparkles size={16} />
        <strong className="ds-grow">Ask AI</strong>
        <Button size="sm" variant="ghost" icon={ChevronRight} aria-label="Collapse" onClick={() => setOpen(false)} />
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: 14 }}>
        {turns.length === 0 ? (
          <div className="ds-small ds-muted" style={{ marginBottom: 12 }}>
            Ask about the numbers on this page. It only reads what you can see here and can't change anything.
          </div>
        ) : null}
        {turns.map((t, i) => (
          <div key={i} style={{ marginBottom: 12, display: "flex", flexDirection: "column", alignItems: t.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: "92%", padding: "8px 11px", borderRadius: 10, whiteSpace: "pre-wrap", fontSize: 13.5, background: t.role === "user" ? "var(--accent, #2563eb)" : "var(--surface-2, rgba(128,128,128,.12))", color: t.role === "user" ? "#fff" : t.error ? "var(--danger, #dc2626)" : "inherit" }}>
              {t.text}
            </div>
            {t.widgets && t.widgets.length > 0 ? (
              <div className="ds-small ds-faint" style={{ marginTop: 4 }}>Used: {t.widgets.map((w) => w.title || w.id).join(", ")}</div>
            ) : null}
            {t.truncated ? <div className="ds-small ds-faint">Only part of this page's data fit; narrow the filters for a sharper answer.</div> : null}
          </div>
        ))}
        {ask.isPending ? <div className="ds-small ds-muted">Thinking… this can take up to a minute.</div> : null}
        <div ref={end} />
      </div>
      <div style={{ padding: 12, borderTop: "1px solid var(--divider)" }}>
        <div className="ds-row" style={{ gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          {suggestions.slice(0, 4).map((s) => (
            <button key={s} type="button" className="ds-chip" disabled={ask.isPending} onClick={() => send(s)}>{s}</button>
          ))}
        </div>
        <form className="ds-row" style={{ gap: 6 }} onSubmit={(e) => { e.preventDefault(); send(text); }}>
          <input className="ds-input" style={{ flex: 1 }} value={text} maxLength={800} placeholder="Ask a question…" onChange={(e) => setText(e.target.value)} aria-label="Your question" />
          <Button type="submit" variant="primary" icon={Send} disabled={ask.isPending || text.trim().length < 2} aria-label="Send" />
        </form>
      </div>
    </aside>
  );
}
